/**
 * Account Roles Resource
 *
 * Thin 1:1 mapping to the account role-management API
 * (`/v1/accounts/{account_id}/members*`). No business logic, no default
 * parameter injection.
 *
 * All three endpoints are gated server-side to the account_owner role (or a
 * platform admin) and are account-scoped: a caller's JWT is scoped to one
 * account, so an owner of A may not manage account B (only a platform admin may
 * address a foreign account). Every method returns the FRESH roster from
 * `account_memberships` —
 * so `assign`/`revoke` refresh the panel in a single round trip.
 *
 * The owner-floor invariant (an account's `account_owner` count may never reach
 * zero) is enforced by the mutation activity, surfacing as a 409 with code
 * `LAST_OWNER_CANNOT_BE_REVOKED`. `assign`/`revoke` translate that into a typed
 * {@link LastOwnerError} so a client can distinguish "you may not" (403 — not an
 * owner) from "you must transfer ownership first" (409 — the floor).
 */

import type { HttpLayer } from '../http.js';
import type { AccountMemberRole, AccountRole } from '../types/index.js';
import { APIError, SDKError } from '../errors.js';

/** Wire code the backend returns when a mutation would leave an account with no owner. */
const LAST_OWNER_CODE = 'LAST_OWNER_CANNOT_BE_REVOKED';

/**
 * Thrown by {@link AccountRolesResource.assign} / {@link
 * AccountRolesResource.revoke} when the mutation would remove the account's last
 * `account_owner` (the owner floor). Distinct from the 403 a
 * non-owner caller gets: a `LastOwnerError` means the caller IS authorized but
 * must transfer ownership first (assign a new owner, then revoke). Carries the
 * originating {@link APIError} on `cause`.
 */
export class LastOwnerError extends SDKError {
  readonly accountId: string;
  readonly userId: string;
  readonly cause: APIError;

  constructor(accountId: string, userId: string, cause: APIError) {
    super(
      `Cannot remove the last account_owner of account ${accountId} (user ${userId}); ` +
        `transfer ownership first by assigning another owner, then revoke`,
    );
    this.name = 'LastOwnerError';
    this.accountId = accountId;
    this.userId = userId;
    this.cause = cause;
  }
}

/**
 * If `err` is the owner-floor 409, rethrow it as a {@link LastOwnerError};
 * otherwise rethrow it unchanged (403, 404, network, etc. propagate as-is).
 */
function rethrowMutationError(
  err: unknown,
  accountId: string,
  userId: string,
): never {
  if (
    err instanceof APIError &&
    err.status === 409 &&
    err.code === LAST_OWNER_CODE
  ) {
    throw new LastOwnerError(accountId, userId, err);
  }
  throw err;
}

export class AccountRolesResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/accounts';

  /**
   * List an account's explicit memberships and effective roles.
   *
   * When invoked via the Shogun `platform_call` executor, `accountId` arrives
   * UNDEFINED at runtime: the account id is an identity field stripped from the
   * generated argSchema, so the executor maps zero positional args and the agent
   * supplies none. In that case the caller's OWN account is resolved from the
   * authenticated session token (`actingAccountId`), scoping the read to the
   * acting account exactly as the roster gate intends
   * (accountRoles.sweep). Direct SDK callers always pass an explicit account.
   *
   * The parameter type stays `string` (its documented contract) — only the
   * agent-dispatch path omits it — hence the narrowing cast to reflect that the
   * value can genuinely be undefined at runtime.
   */
  async list(accountId: string): Promise<AccountMemberRole[]> {
    const acct = (accountId as string | undefined) ?? (await this.http.actingAccountId());
    return this.http.get(`${this.prefix}/${acct}/members`);
  }

  /**
   * Assign (upsert) a principal's account-scoped role. An upsert on the
   * `(org, account, user_id)` key, so `assign(..., 'account_member')` on a
   * sitting owner is a demotion; a demotion that would zero the account's owners
   * is rejected as a {@link LastOwnerError} (409). Returns the fresh roster.
   */
  async assign(
    accountId: string,
    userId: string,
    role: AccountRole,
  ): Promise<AccountMemberRole[]> {
    try {
      return await this.http.put(
        `${this.prefix}/${accountId}/members/${userId}/role`,
        { role },
      );
    } catch (err) {
      rethrowMutationError(err, accountId, userId);
    }
  }

  /**
   * Revoke a principal's role row (they remain on the roster as
   * `account_member`). Revoking the last owner is rejected as a
   * {@link LastOwnerError} (409). Returns the fresh roster.
   */
  async revoke(
    accountId: string,
    userId: string,
  ): Promise<AccountMemberRole[]> {
    try {
      return await this.http.delete(
        `${this.prefix}/${accountId}/members/${userId}/role`,
      );
    } catch (err) {
      rethrowMutationError(err, accountId, userId);
    }
  }
}
